import { v1 } from '@/infrastructure/api/v1/client';
import { listAllIn } from '@/infrastructure/api/v1/lists';
import { sceneToLegacy, type SceneDto } from '@/infrastructure/api/v1/mappers/scenes';
import { assetInstanceToLegacy } from '@/infrastructure/api/v1/mappers/assetInstances';
import { fetchSceneRows } from '@/modules/databaseService/scenes';

// Version number descending with unnumbered versions first, as the generic route's order desc gave them
const byVersionDesc = (a: SceneDto, b: SceneDto): number => {
  if (a.versionNumber === b.versionNumber) return 0;
  if (a.versionNumber == null) return -1;
  if (b.versionNumber == null) return 1;
  return b.versionNumber - a.versionNumber;
};

/** Every version of the given stories as legacy rows, newest version first. */
async function fetchStoryVersions(storyIds: string[]) {
  const result = await listAllIn(storyIds, (storyId, page) =>
    v1.GET('/api/v1/scenes', { params: { query: { storyUniqueId: storyId, sort: '-versionNumber', ...page } } }), { compare: byVersionDesc });
  return { data: result.data?.map(sceneToLegacy) ?? null, error: result.error };
}

/** Every asset instance of the given scene versions as legacy rows. */
async function fetchSceneAssets(sceneIds: string[]) {
  const result = await listAllIn(sceneIds, (sceneId, page) =>
    v1.GET('/api/v1/asset-instances', { params: { query: { sceneUniqueId: sceneId, ...page } } }));
  return { data: result.data?.map(assetInstanceToLegacy) ?? null, error: result.error };
}

// Categories that don't contribute to playback length (anchors, camera, immersal maps, clip-only assets).
// The previous PostgREST `.or(neq...)` filter was a no-op (always true), so these were wrongly counted.
const NON_CONTENT_CATEGORIES = new Set(['immersal', 'camera', 'clipAsset', 'geospatial_anchor']);

// Anchor categories surfaced on the story card (ordered so icons render consistently).
export const ANCHOR_CATEGORIES = ['geospatial_anchor', 'immersal', 'image_anchor'] as const;

const contentEndTimes = (
  assets: Array<{ asset_instance_category?: string | null; asset_instance_end_time?: number | null }>
): number[] =>
  assets
    .filter((a) => !NON_CONTENT_CATEGORIES.has(a.asset_instance_category ?? ''))
    .map((a) => a.asset_instance_end_time || 0);

export class StoryDurationService {
  /**
   * Calculate story duration in minutes based on highest priority version
   * Priority: LIVE > PENDING > most recent DRAFT > most recent ARCHIVED > most recent LOCKED
   * @param storyId - The story/scene origin ID
   * @param storyStatus - Optional status hint, but function will determine best version
   * @returns Duration in minutes, or null if no data found
   */
  async calculateStoryDuration(
    storyId: string,
    storyStatus?: 'pending' | 'live' | 'draft' | 'archived' | 'locked'
  ): Promise<number | null> {
    console.log(`🔍 [StoryDurationService] Starting duration calculation for story: ${storyId}, status hint: ${storyStatus}`);
    
    try {
      // Get ALL scene instances for this story to determine the best one
      const { data: allSceneInstances, error: sceneError } = await fetchSceneRows({ storyUniqueId: [storyId], sort: '-versionNumber' });

      if (sceneError) {
        console.error('❌ [StoryDurationService] Error fetching scene instances for duration:', sceneError);
        return 0;
      }

      if (!allSceneInstances || allSceneInstances.length === 0) {
        console.warn(`⚠️ [StoryDurationService] No scene instances found for story ${storyId}`);
        return 0;
      }

      // Find the highest priority version: LIVE > PENDING > most recent DRAFT > most recent ARCHIVED > most recent LOCKED
      let selectedInstance;

      // First priority: LIVE
      selectedInstance = allSceneInstances.find((instance) => instance.scene_instance_status === 'live');

      // Second priority: PENDING (most recent if multiple)
      if (!selectedInstance) {
        selectedInstance = allSceneInstances.find((instance) => instance.scene_instance_status === 'pending');
      }

      // Third priority: most recent DRAFT
      if (!selectedInstance) {
        selectedInstance = allSceneInstances.find((instance) => instance.scene_instance_status === 'draft' || instance.scene_instance_status === 'template');
      }

      // Fourth priority: most recent ARCHIVED
      if (!selectedInstance) {
        selectedInstance = allSceneInstances.find((instance) => instance.scene_instance_status === 'archived');
      }

      // Fifth priority: most recent LOCKED
      if (!selectedInstance) {
        selectedInstance = allSceneInstances.find((instance) => instance.scene_instance_status === 'locked');
      }

      if (!selectedInstance) {
        console.warn(`⚠️ [StoryDurationService] No valid scene instance found for story ${storyId}`);
        return 0;
      }

      const sceneInstanceId = selectedInstance.scene_instance_id;
      console.log(`🎯 [StoryDurationService] Using scene instance: ${sceneInstanceId} for duration calculation`);

      // Get asset instances for this scene instance (non-content categories filtered in JS)
      const { data: assetInstances, error: assetError } = await fetchSceneAssets([sceneInstanceId]);

      if (assetError) {
        console.error('❌ [StoryDurationService] Error fetching asset instances for duration:', assetError);
        return 0;
      }

      if (!assetInstances || assetInstances.length === 0) {
        console.warn(`⚠️ [StoryDurationService] No asset instances found for scene instance ${sceneInstanceId}`);
        return 0;
      }

      // Latest end time across content assets (stored in seconds)
      const endTimes = contentEndTimes(assetInstances);
      const maxEndTime = endTimes.length ? Math.max(...endTimes) : 0;

      // asset_instance_end_time is in seconds; convert to whole minutes for display
      const durationMinutes = Math.ceil(maxEndTime / 60);

      return durationMinutes;
    } catch (error) {
      console.error('💥 [StoryDurationService] Error calculating story duration:', error);
      return 0; // Return 0 instead of null
    }
  }

  /**
   * Batch fetch story statuses and durations with individual story durations returned
   * Optimized to make only 2 DB calls total regardless of number of stories
   * @param sceneOriginIds - Array of story IDs
   * @returns Object with story statuses, individual durations, and total duration
   */
  async batchCalculateStoryDurationsDetailed(sceneOriginIds: string[]): Promise<{
    storyStatuses: Record<string, 'live' | 'draft' | 'archived' | 'pending' | 'locked' | 'template'>;
    storyDurations: Record<string, number>; // Duration in minutes
    storyDraftInfo: Record<string, { draftCount: number; draftSceneInstanceId: string | null }>;
    storyAnchorTypes: Record<string, string[]>; // Anchor categories present in the best instance
    storyUpdatedAt: Record<string, string | null>; // Latest scene_instance.updated_at (tracks edits)
    totalDurationSeconds: number;
    // False when the assets could not be read: durations are unknown, not zero
    durationsKnown: boolean;
    sceneIds: string[];
  }> {
    // A failed versions read throws: statuses read from nothing would all say draft
    const { data: sceneInstances, error: sceneInstancesError } = await fetchStoryVersions(sceneOriginIds);
    if (sceneInstancesError) throw sceneInstancesError;

    try {
      if (!sceneInstances || sceneInstances.length === 0) {
        console.warn('⚠️ [StoryDurationService] No scene instances found for batch calculation');
        return {
          storyStatuses: {},
          storyDurations: {},
          storyDraftInfo: {},
          storyAnchorTypes: {},
          storyUpdatedAt: {},
          totalDurationSeconds: 0,
          durationsKnown: true,
          sceneIds: []
        };
      }

      // Group scene instances by story and find the best instance for each story
      const instancesByStory: Record<string, typeof sceneInstances> = {};
      const bestInstanceByStory: Record<string, typeof sceneInstances[0]> = {};
      
      sceneInstances.forEach((instance) => {
        if (!instance.scene_origin_id) return;
        if (!instancesByStory[instance.scene_origin_id]) {
          instancesByStory[instance.scene_origin_id] = [];
        }
        instancesByStory[instance.scene_origin_id].push(instance);
      });

      // For each story, find the highest priority instance for duration calculation
      for (const sceneOriginId of sceneOriginIds) {
        const instances = instancesByStory[sceneOriginId] || [];
        if (instances.length === 0) continue;

        // Priority: LIVE > PENDING > DRAFT > ARCHIVED > LOCKED
        const selectedInstance =
          instances.find((i) => i.scene_instance_status === 'live') ||
          instances.find((i) => i.scene_instance_status === 'pending') ||
          instances.find((i) => i.scene_instance_status === 'draft' || i.scene_instance_status === 'template') ||
          instances.find((i) => i.scene_instance_status === 'archived') ||
          instances.find((i) => i.scene_instance_status === 'locked');

        if (selectedInstance) {
          bestInstanceByStory[sceneOriginId] = selectedInstance;
        }
      }

      // One read of the assets of ALL of the stories' versions serves both the durations (best
      // version) and the latest edit time (edits land on a draft, not the "best" one).
      // asset_instance.updated_at is trigger-bumped on edits (move/resize/retime an asset), while
      // scene_instance.updated_at is not. v1 cannot sort asset instances by updatedAt, so there is
      // no cheaper read of the newest edit. Non-content categories are filtered in JS.
      const allSceneInstanceIds = sceneInstances.map((s) => s.scene_instance_id).filter(Boolean);
      const { data: assetInstances, error: assetError } = await fetchSceneAssets(allSceneInstanceIds);

      if (assetError) {
        console.error('❌ [StoryDurationService] Error fetching asset instances:', assetError);
      }

      // Group asset instances by scene instance
      const assetsBySceneInstance: Record<string, NonNullable<typeof assetInstances>> = {};
      assetInstances?.forEach((asset) => {
        if (!asset.scene_instance_id) return;
        if (!assetsBySceneInstance[asset.scene_instance_id]) {
          assetsBySceneInstance[asset.scene_instance_id] = [];
        }
        assetsBySceneInstance[asset.scene_instance_id].push(asset);
      });

      // Latest edit time per story
      const originBySceneInstance: Record<string, string> = {};
      sceneInstances.forEach((s) => {
        if (s.scene_instance_id && s.scene_origin_id) originBySceneInstance[s.scene_instance_id] = s.scene_origin_id;
      });
      const latestAssetEditByOrigin: Record<string, string> = {};
      (assetInstances ?? []).forEach((row) => {
        const origin = originBySceneInstance[row.scene_instance_id as string];
        const ts = row.updated_at;
        if (!origin || !ts) return;
        if (!latestAssetEditByOrigin[origin] || ts > latestAssetEditByOrigin[origin]) {
          latestAssetEditByOrigin[origin] = ts;
        }
      });

      // Calculate statuses and durations for all stories
      const storyStatuses: Record<string, 'live' | 'draft' | 'archived' | 'pending' | 'locked' | 'template'> = {};
      const storyDurations: Record<string, number> = {};
      const storyDraftInfo: Record<string, { draftCount: number; draftSceneInstanceId: string | null }> = {};
      const storyAnchorTypes: Record<string, string[]> = {};
      const storyUpdatedAt: Record<string, string | null> = {};
      let totalDurationSeconds = 0;

      for (const sceneOriginId of sceneOriginIds) {
        const instances = instancesByStory[sceneOriginId] || [];

        // Latest edit timestamp: newest of the story's scene_instance.updated_at values and its
        // latest asset edit (asset_instance.updated_at). Beats scene_origin.updated_at, which is
        // only bumped on rename/reorder, never on content edits like moving an asset.
        const updatedTimes = instances
          .map((i) => (i as { updated_at?: string | null }).updated_at)
          .filter((t): t is string => !!t);
        if (latestAssetEditByOrigin[sceneOriginId]) updatedTimes.push(latestAssetEditByOrigin[sceneOriginId]);
        updatedTimes.sort();
        storyUpdatedAt[sceneOriginId] = updatedTimes.length ? updatedTimes[updatedTimes.length - 1] : null;

        // Draft info for the "Open Story" button (instances are ordered by version desc).
        // Mirrors the previous per-card scene_instances query that caused the N+1.
        const draftInstances = instances.filter(
          (i) => i.scene_instance_status === 'draft' || i.scene_instance_status === 'template'
        );
        storyDraftInfo[sceneOriginId] = {
          draftCount: draftInstances.length,
          draftSceneInstanceId: draftInstances[0]?.scene_instance_id ?? null
        };

        // Determine display status based on available instances (for UI display)
        const hasLive = instances.some((i) => i.scene_instance_status === 'live');
        const hasDraft = instances.some((i) => i.scene_instance_status === 'draft');
        const hasTemplate = instances.some((i) => i.scene_instance_status === 'template');
        const hasPending = instances.some((i) => i.scene_instance_status === 'pending');
        const hasArchived = instances.some((i) => i.scene_instance_status === 'archived');
        const hasLocked = instances.some((i) => i.scene_instance_status === 'locked');

        let storyStatus: 'live' | 'draft' | 'archived' | 'pending' | 'locked' | 'template';
        // Priority order: LOCKED > LIVE > PENDING > TEMPLATE > DRAFT > ARCHIVED
        if (hasLocked) {
          storyStatus = 'locked';
        } else if (hasLive) {
          storyStatus = 'live';
        } else if (hasPending) {
          storyStatus = 'pending';
        } else if (hasTemplate) {
          storyStatus = 'template';
        } else if (hasDraft) {
          storyStatus = 'draft';
        } else if (hasArchived) {
          storyStatus = 'archived';
        } else {
          storyStatus = 'draft'; // Default for stories with no instances
        }

        storyStatuses[sceneOriginId] = storyStatus;

        // Calculate duration from pre-fetched asset instances
        const bestInstance = bestInstanceByStory[sceneOriginId];
        let durationMinutes = 0;
        let anchorTypes: string[] = [];
        if (bestInstance) {
          const assets = assetsBySceneInstance[bestInstance.scene_instance_id] || [];
          anchorTypes = ANCHOR_CATEGORIES.filter((cat) =>
            assets.some((a) => a.asset_instance_category === cat)
          );
          if (assets.length > 0) {
            // asset_instance_end_time is in seconds
            const endTimes = contentEndTimes(assets);
            const maxEndTime = endTimes.length ? Math.max(...endTimes) : 0;
            durationMinutes = Math.ceil(maxEndTime / 60);
            // Project total = sum of the per-story rounded minutes, so the header matches the cards
            totalDurationSeconds += durationMinutes * 60;
          }
        }
        storyDurations[sceneOriginId] = durationMinutes;
        storyAnchorTypes[sceneOriginId] = anchorTypes;
      }

      return {
        storyStatuses,
        storyDurations,
        storyDraftInfo,
        storyAnchorTypes,
        storyUpdatedAt,
        totalDurationSeconds,
        durationsKnown: !assetError,
        sceneIds: allSceneInstanceIds
      };
    } catch (error) {
      console.error('💥 [StoryDurationService] Error batch calculating detailed story durations:', error);
      throw error;
    }
  }

  /**
   * Format duration for display
   * @param durationMinutes - Duration in minutes
   * @returns Formatted duration string
   */
  formatDuration(durationMinutes: number | null): string {
    if (durationMinutes === null || durationMinutes === 0) {
      return '0 min';
    }

    if (durationMinutes < 60) {
      return `${durationMinutes} min`;
    }

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (minutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${minutes}m`;
  }
}
